import { useEffect, useRef, useState } from "react";
import { AlertTriangle, PackagePlus, Plus, Trash2 } from "lucide-react";

import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEffectivePrices } from "@/hooks/useCustomerPricing";
import { useCustomers } from "@/hooks/useCustomers";
import { useProducts } from "@/hooks/useProducts";
import { useToast } from "@/hooks/use-toast";
import { useCreateOrderV2 } from "@/hooks/useOrdersV2";
import { isApiError } from "@/lib/queryClient";
import type { EffectivePriceEntry, OrderItemV2, Product, SimilarOrderSummary } from "@/lib/types";
import { formatProductDetail, formatProductLabel } from "@/lib/productLabel";
import { getTaiwanBusinessDateString } from "@/lib/taiwanDate";

interface OrderFormProps {
  onCreated?: () => void;
}

// K-06：相似訂單警示對話框用的狀態中文顯示（同 pages/SignaturePad.tsx 的慣例）。
const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: "待處理",
  preparing: "備貨中",
  ready_to_ship: "待出貨",
  shipping: "配送中",
  delivered: "已送達",
  archived: "已封存",
};

// C2：單價的來源。只存在前端表單狀態，送單時不會帶出去（handleSubmit 逐欄挑選）。
// "manual"＝使用者手動輸入（含手動清空），任何自動重新帶入都不可覆蓋它；null＝查無價格而留空。
type PriceOrigin = "customer" | "product" | "manual" | null;
type OrderFormItem = OrderItemV2 & { priceOrigin: PriceOrigin };

// J-05 殘項 C：單價來源標示文字。
const PRICE_SOURCE_LABELS: Record<"customer" | "product" | "manual", string> = {
  customer: "客戶專屬價",
  product: "產品標準價",
  manual: "手動輸入",
};

// R7b：單價必填。0 是合法的成交價（樣品／換貨／折讓），「沒有單價」只指沒填。
const UNIT_PRICE_REQUIRED_HINT = "請輸入成交價；樣品／換貨／折讓請輸入 0。";
// 已加入的品項仍可能變成沒有單價：改客戶或出貨日期後，該日期查無生效價（C2 會清掉自動帶入的價）。
const MISSING_PRICE_WARNING =
  "此品項沒有單價，訂單無法送出：請刪除後輸入成交價重新加入（樣品／換貨／折讓請輸入 0）。";

/** R7b：有沒有單價一律用這個判斷，不可用 truthy（0 是有效單價）。 */
function hasUnitPrice(item: { unitPrice?: number | null }): item is { unitPrice: number } {
  return item.unitPrice !== null && item.unitPrice !== undefined;
}

// C2：查價失敗（連線／伺服器錯誤）要跟「查無價格」分開說，否則使用者會把有價的品項當成未定價送出。
const PRICE_LOOKUP_ERROR_MESSAGE = "查價失敗，請重試或手動輸入單價";

/** 在有效售價清單裡找品項；價格不是有限正常數字時視為查無價格（不把壞資料帶進表單）。 */
function findEffectivePrice(prices: EffectivePriceEntry[], productCode: string) {
  const match = prices.find((price) => price.productCode === productCode);
  if (!match) {
    return null;
  }

  const value = parseFloat(match.price);
  return Number.isFinite(value) ? { text: match.price, value, source: match.source } : null;
}

export default function OrderForm({ onCreated }: OrderFormProps) {
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [orderDate, setOrderDate] = useState<string>(() => getTaiwanBusinessDateString());
  const [productQuery, setProductQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  // J-05 殘項 C：目前這筆待加入品項的單價來源標示；手動改過時 priceManuallyEdited=true，
  // 顯示「手動輸入」而非 fallback 來源，且不再被 fallback 覆蓋。
  const [priceSource, setPriceSource] = useState<"customer" | "product" | null>(null);
  const [priceManuallyEdited, setPriceManuallyEdited] = useState(false);
  const [items, setItems] = useState<OrderFormItem[]>([]);
  const [shippingMethod, setShippingMethod] = useState<"self_delivery" | "logistics" | "">("");
  const [notes, setNotes] = useState("");
  // K-06：409 SIMILAR_ORDER_EXISTS 時暫存既有相似訂單清單，null 代表對話框關閉。
  const [similarOrders, setSimilarOrders] = useState<SimilarOrderSummary[] | null>(null);

  const { toast } = useToast();
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const { products, searchProducts, loadingStatus } = useProducts();
  const { data: customersData } = useCustomers({ isActive: "true", limit: 100 });
  // C2：單價以「出貨日期」當天生效的價為準（與月結推算同口徑），不是今天。
  const {
    data: effectivePricesData,
    isFetching: isFetchingPrices,
    isError: isPriceLookupError,
    refetch: refetchPrices,
  } = useEffectivePrices(customerId, orderDate);
  const createOrderV2 = useCreateOrderV2();

  const customers = customersData?.data || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        (event.target as HTMLElement).id !== "productCode"
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // C2：只採用「客戶與出貨日期都與表單目前值相符」的價格資料；載入中、查詢失敗或不相符時視為沒有資料，
  // 寧可留空也不顯示別的日期（或別的客戶）的價。
  const freshPrices =
    effectivePricesData &&
    effectivePricesData.customerId === customerId &&
    effectivePricesData.asOfDate === orderDate
      ? effectivePricesData
      : undefined;

  // FIX-07 / J-05 殘項 C / C2：切換客戶或出貨日期時重新解析待加入品項的單價（客戶專屬價 → 產品標準價）；
  // 使用者手動改過的單價不被覆蓋（priceManuallyEdited 守門）。
  useEffect(() => {
    if (!selectedProduct || priceManuallyEdited) {
      return;
    }

    const match = freshPrices ? findEffectivePrice(freshPrices.prices, selectedProduct.code) : null;
    if (match) {
      setUnitPrice(match.text);
      setPriceSource(match.source);
    } else {
      setUnitPrice("");
      setPriceSource(null);
    }
  }, [freshPrices, selectedProduct, priceManuallyEdited]);

  // C2：已加入的品項——客戶或出貨日期變更（或價格資料更新）後，非手動輸入的單價依新資料重新帶入，
  // 並以 toast 明確告知筆數（絕不靜默改價）；手動輸入的品項一律不動。
  useEffect(() => {
    if (!freshPrices) {
      return;
    }

    let repricedCount = 0;
    let clearedCount = 0;
    const nextItems = items.map((item) => {
      if (item.priceOrigin === "manual") {
        return item;
      }

      const match = findEffectivePrice(freshPrices.prices, item.productCode);
      const nextUnitPrice = match ? match.value : undefined;
      const nextOrigin: PriceOrigin = match ? match.source : null;
      if ((item.unitPrice ?? undefined) === nextUnitPrice && item.priceOrigin === nextOrigin) {
        return item;
      }

      if (match) {
        repricedCount += 1;
      } else {
        clearedCount += 1;
      }
      return { ...item, unitPrice: nextUnitPrice, priceOrigin: nextOrigin };
    });

    if (repricedCount + clearedCount > 0) {
      setItems(nextItems);
      // 如實告知：重新帶入的與「該日期查無價格、已改為未定價」的分開說。
      const title =
        clearedCount === 0
          ? `${repricedCount} 筆品項單價已重新帶入`
          : repricedCount === 0
            ? `${clearedCount} 筆品項查無該日期的價格`
            : `${repricedCount} 筆已重新帶入、${clearedCount} 筆查無該日期的價格`;
      const details = [
        repricedCount > 0 ? `${repricedCount} 筆已依出貨日期 ${freshPrices.asOfDate} 的生效價重新帶入` : null,
        clearedCount > 0 ? `${clearedCount} 筆在出貨日期 ${freshPrices.asOfDate} 查無生效價，已改為未定價` : null,
      ].filter(Boolean);
      toast({
        title,
        description: `${details.join("；")}；手動輸入的單價未變更。`,
      });
    }
  }, [freshPrices, items, toast]);

  const handleProductSearch = (value: string) => {
    setProductQuery(value);
    setSelectedProduct(null);
    setShowSuggestions(Boolean(value.trim()));
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setProductQuery(formatProductLabel(product));
    setShowSuggestions(false);
    // 選新產品＝全新的一筆待加入品項，重置手動旗標讓 fallback 效果重新解析單價。
    setPriceManuallyEdited(false);
    setPriceSource(null);
  };

  const handleAddItem = () => {
    if (!selectedProduct) {
      toast({ title: "請先選擇產品", variant: "destructive" });
      return;
    }

    // FIX-07: 防止 NaN 穿透（parseFloat("abc") 回傳 NaN，NaN <= 0 為 false 導致驗證誤判）
    const qty = parseFloat(quantity);
    if (!quantity || Number.isNaN(qty) || qty <= 0) {
      toast({ title: "請輸入正確數量（需為正數）", variant: "destructive" });
      return;
    }

    // R7b：單價必填（後端 createOrderV2Schema 同規則）。查價失敗時同樣要手動輸入，不能以未定價加入。
    if (unitPrice.trim() === "") {
      toast({
        title: "請輸入單價",
        description: priceLookupFailed
          ? "查價失敗：請按「重試查價」，或手動輸入成交價（樣品／換貨／折讓請輸入 0）。"
          : `此品項在出貨日期查無價格。${UNIT_PRICE_REQUIRED_HINT}`,
        variant: "destructive",
      });
      return;
    }

    // FIX-07: 單價上限檢查，防止異常值進入訂單；R7b：0 是合法的成交價，只擋負數。
    const price = parseFloat(unitPrice);
    if (!Number.isFinite(price) || price < 0 || price >= 1_000_000) {
      toast({ title: "單價不合法（需介於 0 ~ 1,000,000）", variant: "destructive" });
      return;
    }

    setItems((current) => [
      ...current,
      {
        productCode: selectedProduct.code,
        productName: selectedProduct.name,
        quantity: qty,
        unitPrice: price,
        unit: selectedProduct.unit || "KG",
        priceOrigin: priceManuallyEdited ? "manual" : priceSource,
      },
    ]);

    setProductQuery("");
    setQuantity("");
    setUnitPrice("");
    setPriceSource(null);
    setPriceManuallyEdited(false);
    setSelectedProduct(null);
  };

  const handleRemoveItem = (index: number) => {
    setItems((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleSubmit = async (confirmSimilar = false) => {
    if (!customerId) {
      toast({ title: "請先選擇客戶", variant: "destructive" });
      return;
    }

    if (!orderDate) {
      toast({ title: "請先選擇日期", variant: "destructive" });
      return;
    }

    if (items.length === 0) {
      toast({ title: "請至少加入一個品項", variant: "destructive" });
      return;
    }

    // C2：自動帶入的單價必須已依目前的客戶＋出貨日期確認過；查價中或查價失敗時不送出別的日期的價。
    if (!freshPrices && items.some((item) => item.priceOrigin === "customer" || item.priceOrigin === "product")) {
      toast({
        title: "單價尚未依出貨日期確認",
        description: "查價完成後再送出；若查價失敗請按「重試查價」，或刪除該品項後以手動輸入單價重新加入。",
        variant: "destructive",
      });
      return;
    }

    // R7b：每個品項都必須有單價。已加入的品項可能在改客戶／出貨日期後被清成未定價，所以送出前再擋一次。
    const unpricedItems = items.filter((item) => !hasUnitPrice(item));
    if (unpricedItems.length > 0) {
      toast({
        title: `有 ${unpricedItems.length} 個品項沒有單價，無法送出`,
        description: `${unpricedItems.map((item) => item.productCode).join("、")}：請刪除後輸入成交價重新加入（樣品／換貨／折讓請輸入 0）。`,
        variant: "destructive",
      });
      return;
    }

    try {
      await createOrderV2.mutateAsync({
        customerId,
        orderDate,
        shippingMethod: shippingMethod || undefined,
        notes: notes || undefined,
        confirmSimilar: confirmSimilar || undefined,
        items: items.map((item) => ({
          productCode: item.productCode,
          productName: item.productName,
          quantity: item.quantity,
          unitPrice: item.unitPrice as number,
          unit: item.unit,
        })),
      });

      toast({ title: "訂單建立完成" });
      setSimilarOrders(null);
      setItems([]);
      setNotes("");
      setShippingMethod("");
      setProductQuery("");
      setQuantity("");
      setUnitPrice("");
      setPriceSource(null);
      setPriceManuallyEdited(false);
      setSelectedProduct(null);
      onCreated?.();
    } catch (error) {
      // K-06：已有相似訂單——不當成一般錯誤 toast，改用對話框讓使用者確認是否仍要建立。
      if (isApiError(error) && error.code === "SIMILAR_ORDER_EXISTS") {
        setSimilarOrders(error.data?.similarOrders ?? []);
        return;
      }

      toast({
        title: "訂單建立失敗",
        description: error instanceof Error ? error.message : "發生未預期錯誤",
        variant: "destructive",
      });
    }
  };

  const handleConfirmSimilar = () => {
    setSimilarOrders(null);
    void handleSubmit(true);
  };

  const handleCancelSimilar = () => {
    setSimilarOrders(null);
  };

  const filteredProducts = searchProducts(productQuery);
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  // C2（S1）：自動帶入的單價只有在「有與目前客戶＋出貨日期相符的價格資料」時才算數；查價中、查價失敗或
  // 日期清空期間，state 裡留著的是別的日期／客戶的價——顯示與合計一律不用它，改顯示「待確認」。
  const isPricePendingConfirmation = (item: OrderFormItem) =>
    !freshPrices && (item.priceOrigin === "customer" || item.priceOrigin === "product");
  const hasPendingConfirmationItems = items.some(isPricePendingConfirmation);
  // C2（S2）：查價失敗（不是查無價格）。重試中（isFetching）先不算失敗，改顯示「查價中…」。
  const priceLookupFailed = isPriceLookupError && !isFetchingPrices && !freshPrices;
  const totalAmount = items.reduce((sum, item) => sum + (hasUnitPrice(item) ? item.quantity * item.unitPrice : 0), 0);
  const itemsMissingPriceCount = items.filter((item) => !hasUnitPrice(item)).length;
  // C2：查價中且尚未手動輸入時，暫停「加入」，避免把還沒帶入價格的品項當成「未定價」加進去。
  const isResolvingPrice = isFetchingPrices && !priceManuallyEdited;
  const priceSourceLabel = priceManuallyEdited
    ? PRICE_SOURCE_LABELS.manual
    : isResolvingPrice && selectedProduct
      ? "查價中…"
      : priceSource
        ? PRICE_SOURCE_LABELS[priceSource]
        : null;

  return (
    <div className="relative mb-8 overflow-hidden rounded-xl border border-line border-t-4 border-t-progress bg-surface p-6 shadow-sm">
      <div className="pointer-events-none absolute right-0 top-0 h-32 w-32 rounded-bl-full bg-progress-tint" />

      <h2 className="relative z-10 mb-6 flex items-center gap-2 text-xl font-bold text-ink-strong">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-progress-tint">
          <PackagePlus className="h-4 w-4 text-progress" />
        </div>
        建立新訂單
      </h2>

      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="inline-block w-32 text-lg font-medium text-ink">客戶</label>
        <select
          id="orderCustomerSelect"
          value={customerId || ""}
          onChange={(event) => setCustomerId(event.target.value ? Number(event.target.value) : null)}
          className="glass-input h-11 w-full rounded-md sm:w-64"
        >
          <option value="">請選擇客戶</option>
          {customers.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
              {customer.code ? ` (${customer.code})` : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="inline-block w-32 text-lg font-medium text-ink">出貨日期</label>
        <Input
          id="orderDateInput"
          type="date"
          value={orderDate}
          onChange={(event) => setOrderDate(event.target.value)}
          className="glass-input h-11 w-full sm:w-64"
        />
      </div>

      <div className="mb-5 rounded-lg border border-line-strong/50 bg-raised/50 p-4">
        <h3 className="mb-3 text-sm font-medium text-ink-muted">新增品項</h3>

        <div className="flex flex-col items-end gap-3 lg:flex-row">
          <div className="relative flex-1">
            <label className="mb-1 block text-xs text-ink-muted">產品</label>
            <Input
              id="productCode"
              placeholder="輸入產品代碼或名稱搜尋"
              value={productQuery}
              onChange={(event) => handleProductSearch(event.target.value)}
              onFocus={() => productQuery.trim() && setShowSuggestions(true)}
              className="glass-input h-10 w-full"
            />

            {showSuggestions && filteredProducts.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute z-50 mt-1 max-h-[250px] w-full overflow-y-auto rounded-lg border border-line-strong bg-surface text-sm shadow-2xl"
              >
                {filteredProducts.map((product, index) => (
                  <div
                    key={`${product.code}-${index}`}
                    data-testid={`productSuggestion-${product.code}`}
                    className="flex cursor-pointer flex-wrap items-center gap-1.5 border-b border-line p-2.5 transition-colors hover:bg-raised"
                    onClick={() => handleSelectProduct(product)}
                  >
                    <span className="font-bold text-progress">{product.code}</span>
                    {/* J-04h：名稱跟代碼一樣就不重複，顏色才是辨認依據 */}
                    {formatProductDetail(product) && <span className="text-ink">{formatProductDetail(product)}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="w-28">
            <label className="mb-1 block text-xs text-ink-muted">數量 (kg)</label>
            <Input
              id="orderQuantityInput"
              type="number"
              placeholder="數量"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              className="glass-input h-10"
            />
          </div>

          <div className="w-28">
            <label className="mb-1 block text-xs text-ink-muted">單價</label>
            <Input
              id="orderUnitPriceInput"
              type="number"
              placeholder="必填"
              value={unitPrice}
              onChange={(event) => {
                setUnitPrice(event.target.value);
                setPriceManuallyEdited(true);
                setPriceSource(null);
              }}
              className="glass-input h-10"
            />
            {priceSourceLabel && (
              <span
                data-testid="unitPriceSourceLabel"
                className="mt-1 block text-[11px] text-ink-muted"
              >
                {priceSourceLabel}
              </span>
            )}
            {!priceSourceLabel && priceLookupFailed && selectedProduct && (
              <span
                data-testid="unitPriceLookupError"
                className="mt-1 block text-[11px] font-medium text-warn"
              >
                查價失敗
              </span>
            )}
          </div>

          <Button id="orderAddItemBtn" onClick={handleAddItem} disabled={isResolvingPrice} className="h-10 shrink-0 bg-action px-4 text-white hover:bg-action-hover">
            <Plus className="mr-1 h-4 w-4" />
            加入
          </Button>
        </div>

        {priceLookupFailed && (
          <div
            data-testid="orderFormPriceLookupError"
            className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-warn/40 bg-warn-tint p-3 text-sm text-warn"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{PRICE_LOOKUP_ERROR_MESSAGE}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void refetchPrices()}
              className="ml-auto h-8 border-warn/40 bg-transparent text-warn hover:bg-warn-tint hover:text-warn"
            >
              重試查價
            </Button>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="mb-5 overflow-hidden rounded-xl border border-line-strong/50 bg-surface/50">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-line-strong bg-raised/80">
                <th className="p-3 font-medium text-ink-muted">產品代碼</th>
                <th className="p-3 font-medium text-ink-muted">產品名稱</th>
                <th className="p-3 text-right font-medium text-ink-muted">數量</th>
                <th className="p-3 text-right font-medium text-ink-muted">單價</th>
                <th className="p-3 text-right font-medium text-ink-muted">小計</th>
                <th className="w-16 p-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={`${item.productCode}-${index}`} className="border-b border-line-strong/50 hover:bg-raised/50">
                  <td className="p-3 font-medium text-progress">{item.productCode}</td>
                  <td className="p-3 text-ink">{item.productName}</td>
                  <td className="p-3 text-right text-ink">
                    {item.quantity} {item.unit || "KG"}
                  </td>
                  <td className="p-3 text-right text-ink">
                    {isPricePendingConfirmation(item) ? (
                      <span
                        data-testid={`orderItemPricePending-${index}`}
                        className="text-xs text-ink-muted"
                        title="單價尚未依目前的客戶與出貨日期確認"
                      >
                        待確認
                      </span>
                    ) : hasUnitPrice(item) ? (
                      <>
                        {`$${item.unitPrice}`}
                        {item.priceOrigin && (
                          <span
                            data-testid={`orderItemPriceOrigin-${index}`}
                            className="block text-[11px] text-ink-muted"
                          >
                            {PRICE_SOURCE_LABELS[item.priceOrigin]}
                          </span>
                        )}
                      </>
                    ) : (
                      <span
                        className="inline-flex items-center justify-end gap-1 text-xs font-medium text-warn"
                        title={priceLookupFailed ? PRICE_LOOKUP_ERROR_MESSAGE : MISSING_PRICE_WARNING}
                      >
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                        未定價
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right font-medium text-done">
                    {hasUnitPrice(item) && !isPricePendingConfirmation(item)
                      ? `$${(item.quantity * item.unitPrice).toLocaleString()}`
                      : "-"}
                  </td>
                  <td className="p-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                      className="h-7 w-7 p-0 text-block hover:bg-block-tint"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-line-strong bg-raised/60">
                <td colSpan={2} className="p-3 font-medium text-ink">
                  合計（{items.length} 項）
                </td>
                <td className="p-3 text-right font-bold text-ink-strong">{totalQuantity} kg</td>
                <td className="p-3" />
                <td data-testid="orderFormTotalAmount" className="p-3 text-right font-bold text-done">
                  {hasPendingConfirmationItems ? (
                    <span className="text-xs font-normal text-ink-muted">待確認</span>
                  ) : totalAmount > 0 || itemsMissingPriceCount === 0 ? (
                    `$${totalAmount.toLocaleString()}`
                  ) : (
                    "-"
                  )}
                </td>
                <td className="p-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="inline-block w-32 text-lg font-medium text-ink">配送方式</label>
        <div className="flex gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-ink">
            <input
              id="orderShippingUnspecified"
              type="radio"
              name="shipping"
              value=""
              checked={shippingMethod === ""}
              onChange={() => setShippingMethod("")}
              className="accent-action"
            />
            <span className="text-sm">未指定</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-ink">
            <input
              id="orderShippingSelfDelivery"
              type="radio"
              name="shipping"
              value="self_delivery"
              checked={shippingMethod === "self_delivery"}
              onChange={() => setShippingMethod("self_delivery")}
              className="accent-action"
            />
            <span className="text-sm">自載</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-ink">
            <input
              id="orderShippingLogistics"
              type="radio"
              name="shipping"
              value="logistics"
              checked={shippingMethod === "logistics"}
              onChange={() => setShippingMethod("logistics")}
              className="accent-action"
            />
            <span className="text-sm">物流</span>
          </label>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-start">
        <label className="inline-block w-32 pt-2 text-lg font-medium text-ink">備註</label>
        <textarea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="可填寫出貨或客戶備註"
          rows={2}
          className="glass-input w-full resize-none rounded-md sm:w-64"
        />
      </div>

      {itemsMissingPriceCount > 0 && !priceLookupFailed && (
        <div
          data-testid="orderFormMissingPriceWarning"
          className="relative z-10 mb-4 flex items-center gap-2 rounded-md border border-warn/40 bg-warn-tint p-3 text-sm text-warn"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            有 {itemsMissingPriceCount} 個品項沒有單價，訂單無法送出：請刪除該品項後輸入成交價重新加入（樣品／換貨／折讓請輸入 0）。
          </span>
        </div>
      )}

      <div className="relative z-10 mt-2 flex items-center gap-4">
        <Button
          id="orderSubmitBtn"
          onClick={() => handleSubmit()}
          disabled={createOrderV2.isPending}
          className="h-11 rounded-lg border-none bg-done px-8 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:bg-done/90 disabled:opacity-50"
        >
          {createOrderV2.isPending ? "建立中..." : "送出訂單"}
        </Button>

        <div className="text-sm text-ink-muted">{loadingStatus}</div>
      </div>

      <ConfirmDialog
        isOpen={similarOrders !== null}
        message={
          <div className="text-left text-base">
            <p className="mb-3 font-medium">
              已有相似訂單（同客戶、同日期、品項與數量相同）：
            </p>
            <ul className="mb-3 max-h-40 space-y-1 overflow-y-auto text-sm text-ink-muted">
              {(similarOrders ?? []).map((order) => (
                <li key={order.id}>
                  {new Date(order.createdAt).toLocaleString("zh-TW")}
                  {" ・ "}
                  {ORDER_STATUS_LABELS[order.status] || order.status}
                  {order.createdByName ? ` ・ ${order.createdByName}` : ""}
                </li>
              ))}
            </ul>
            <p className="text-sm">確定仍要建立這筆新訂單嗎？</p>
          </div>
        }
        onConfirm={handleConfirmSimilar}
        onCancel={handleCancelSimilar}
      />
    </div>
  );
}
